import { useEffect, useState } from "react";
import api from '../config/api';
import Container from "@mui/material/Container";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { Chip, Divider, Paper, Alert, AlertTitle, CircularProgress } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import useStore from "../config/store";
import OrganizationUserGrid from "./OrganizationUserGrid"; // Nuevo componente
import UserPasswordModal from "../user/UserPasswordModal";
import UserProjectsModal from "./UserProjectsModal"; // Nuevo modal
import OrganizationUserModal from "./OrganizationUserModal"; // Modal específico para org
import { useTranslation } from 'react-i18next';

const OrganizationUsersLayout = () => {
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [counter, setCounter] = useState(0);

  const [openProjectsModal, setOpenProjectsModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState("add");

  // Estados para verificación de límites de uso
  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  const { authToken, showAlert, organization, organizationName, user } = useStore.getState();
  const { t } = useTranslation();

  // Verificar límites de uso al cargar el componente
  useEffect(() => {
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [organization, authToken]);

  const checkUsageLimits = async () => {
    if (!organization || !authToken) return;

    try {
      setLoadingUsageCheck(true);
      const { data } = await api.get(
        `/organizations/${organization}/usage/check?action=users`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (data.response && data.response.status_response === "OK") {
        setUsageCheck(data.response);
      } else {
        console.error("Error al verificar límites de uso:", data);
        setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
      }
    } catch (error) {
      console.error("Error al verificar límites de uso:", error);
      setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
    } finally {
      setLoadingUsageCheck(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Obtener proyectos para mapear IDs a nombres
        const projectsResponse = await api.get(`/v2/projects/${organization}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        let projectsMap = {};
        if (projectsResponse.data.response?.message === "All projects were successfully obtained") {
          projectsResponse.data.response.data.forEach(project => {
            projectsMap[project.uid] = project.name;
          });
        }

        // 2. Obtener usuarios de la organización específica
        const userResponse = await api.get(`/organizations/users/${organization}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (userResponse.data.response && userResponse.data.response.status_response === "OK") {
          const orgUsers = userResponse.data.response.data.map((u, index) => ({
            id: index + 1,
            uid: u.uid,
            name: u.name,
            email: u.email,
            rol: u.rol,
            organizations: u.organizations,
            projects: u.projects || [],
            organizationNames: [organizationName], // Solo esta organización
            projectNames: u.projects ? u.projects.map(projectId => projectsMap[projectId] || 'Proyecto Desconocido') : [], // Mapear IDs a nombres
          }));

          setUsers(orgUsers);
          setLoaded(true);
        } else {
          throw new Error("Error en respuesta del servidor");
        }
      } catch (err) {
        console.error(err);
        showAlert(t('users.loadError'), "warning");
        setError(true);
        setLoaded(true);
      }
    };

    loadData();

    // AGREGAR: Actualizar límites de uso cuando cambia el counter
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [counter, authToken, showAlert, organization, organizationName, t]);

  // Renderizar mensaje de límite alcanzado
  // Modificar esta función:
  const renderUsageLimitMessage = () => {
    if (!usageCheck) return null;

    // AGREGAR: No mostrar para usuarios
    if (usageCheck.action === "users") return null;

    const limitText = usageCheck.limit === -1 ? t('users.unlimited') : usageCheck.limit;

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity="warning">
          <AlertTitle>{t('users.usageLimitReached')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('users.usageLimitMessage')}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('users.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('users.users')}
            </Typography>
            {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('users.remainingUsers')}:</strong> {usageCheck.remaining}
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    );
  };

  // Modificar esta función:
  const renderCurrentUsageAlert = () => {
    if (!usageCheck || loadingUsageCheck) return null;

    // AGREGAR: No mostrar para usuarios
    if (usageCheck.action === "users") return null;

    const limitText = usageCheck.limit === -1 ? t('users.unlimited') : usageCheck.limit;
    const severity = usageCheck.canPerform ? 'info' : 'warning';

    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Alert severity={severity}>
          <Typography variant="body2">
            <strong>{t('users.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('users.users')}
          </Typography>
          {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && usageCheck.canPerform && (
            <Typography variant="body2">
              <strong>{t('users.remainingUsers')}:</strong> {usageCheck.remaining}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  };

  return (
    <Box sx={{ pt: "64px" }}>
      <Paper
        sx={{
          p: 1.5,
          pr: 3,
          backgroundColor: "#6A5AF9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "0px !important",
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: "white", ml: 4, fontSize: "1rem" }}
        >
          {t('users.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: "white", pr: 1 }}>
          {organizationName}
        </Typography>
        <Box>
          {/* Espacio para logo si se necesita */}
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Mostrar spinner mientras verifica límites */}
        {loadingUsageCheck ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4, width: '100%' }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>{t('users.checkingUsageLimits')}</Typography>
          </Box>
        ) : (
          <>
            {/* Alert con uso actual O límite alcanzado */}
            {usageCheck && !usageCheck.canPerform ? (
              renderUsageLimitMessage()
            ) : (
              renderCurrentUsageAlert()
            )}

            <Divider sx={{ width: "85%", m: "2rem auto", textAlign: "center" }}>
              <Chip
                sx={{
                  fontSize: "1.5rem",
                  backgroundColor: "transparent",
                  color: "#575757",
                }}
                label={t('users.organizationUsers')}
              />
            </Divider>

            {!loaded ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: "#6A5AF9", mb: 1 }}>
                  {t('users.userLoading')}
                </Typography>
                <LinearProgress color="inherit" sx={{ width: "80%" }} />
              </Box>
            ) : error ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 4,
                }}
              >
                <ErrorIcon sx={{ color: "red", fontSize: "2.5rem", mb: 2 }} />
                <Typography variant="h6" component="h4" sx={{ color: "red" }}>
                  {t('users.loadError')}
                </Typography>
              </Box>
            ) : (
              <OrganizationUserGrid
                users={users}
                onEditProjects={(user) => {
                  setSelectedUser(user);
                  setOpenProjectsModal(true);
                }}
                onEditPassword={(user) => {
                  setSelectedUser(user);
                  setOpenPasswordModal(true);
                }}
                onEditUser={(user) => {
                  setSelectedUser(user);
                  setModalType("edit");
                  setOpenUserModal(true);
                }}
                onDeleteUser={(user) => {
                  setSelectedUser(user);
                  setModalType("delete");
                  setOpenUserModal(true);
                }}
                currentUserId={user.uid}
                currentUserRole={user.rol}
              />
            )}
          </>
        )}

        {/* Botón Fab - Solo mostrar si no hay error de límites y es admin */}
        {["super-admin", "admin-grafton", "admin"].includes(user.rol) && !loadingUsageCheck && usageCheck && usageCheck.canPerform && (
          <Fab
            onClick={() => {
              setSelectedUser(null);
              setModalType("add");
              setOpenUserModal(true);
            }}
            color="warning"
            aria-label="add"
            sx={{
              position: "fixed",
              bottom: 32,
              right: 64,
            }}
          >
            <AddIcon />
          </Fab>
        )}
      </Container>

      {/* Modal para editar proyectos */}
      <UserProjectsModal
        open={openProjectsModal}
        onClose={() => setOpenProjectsModal(false)}
        user={selectedUser}
        authToken={authToken}
        organizationId={organization}
        showAlert={showAlert}
        setCounter={setCounter}
      />

      {/* Modal para cambiar contraseña */}
      <UserPasswordModal
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
        user={selectedUser}
      />

      {/* Modal para crear/editar/eliminar usuario */}
      <OrganizationUserModal
        openModal={openUserModal}
        handleClose={() => setOpenUserModal(false)}
        type={modalType}
        selectedUser={selectedUser}
        authToken={authToken}
        organizationId={organization}
        showAlert={showAlert}
        setCounter={setCounter}
        currentUserRole={user.rol}
      />
    </Box>
  );
};

export default OrganizationUsersLayout;